import { useEffect, useState } from "react";
import { KeyRound, Plus, Copy, Check, Trash2, Terminal, Play, Loader2, ChevronRight, BookOpen } from "lucide-react";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const METHOD_COLOR = {
  GET: "bg-emerald-100 text-emerald-700", POST: "bg-sky-100 text-sky-700",
  PUT: "bg-amber-100 text-amber-700", DELETE: "bg-red-100 text-red-700", PATCH: "bg-violet-100 text-violet-700",
};

function TryIt({ ep }) {
  const pathParams = (ep.path.match(/\{(\w+)\}/g) || []).map((p) => p.slice(1, -1));
  const [pathVals, setPathVals] = useState({});
  const [body, setBody] = useState(ep.body_fields.length ? JSON.stringify(Object.fromEntries(ep.body_fields.map((f) => [f.name, ""])), null, 2) : "");
  const [resp, setResp] = useState(null);
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true); setResp(null);
    let url = ep.path.replace("/api", "");
    pathParams.forEach((p) => { url = url.replace(`{${p}}`, encodeURIComponent(pathVals[p] || "")); });
    const cfg = { method: ep.method.toLowerCase(), url };
    if (["POST", "PUT", "PATCH"].includes(ep.method) && body.trim()) {
      try { cfg.data = JSON.parse(body); } catch { toast.error("Body is not valid JSON"); setSending(false); return; }
    }
    const t0 = performance.now();
    try {
      const r = await api.request(cfg);
      setResp({ status: r.status, ms: Math.round(performance.now() - t0), data: r.data });
    } catch (e) {
      setResp({ status: e.response?.status || "ERR", ms: Math.round(performance.now() - t0), data: e.response?.data || String(e) });
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-3 border-t border-slate-100 bg-slate-50/60 p-4">
      {pathParams.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {pathParams.map((p) => (
            <div key={p} className="space-y-1">
              <Label className="text-xs">{p}</Label>
              <Input data-testid={`tryit-path-${p}`} value={pathVals[p] || ""} onChange={(e) => setPathVals((v) => ({ ...v, [p]: e.target.value }))} placeholder={p} className="h-8 text-xs" />
            </div>
          ))}
        </div>
      )}
      {ep.body_fields.length > 0 && (
        <div className="space-y-1">
          <Label className="text-xs">Request body (JSON) — fields: {ep.body_fields.map((f) => f.name + (f.required ? "*" : "")).join(", ")}</Label>
          <Textarea data-testid="tryit-body" value={body} onChange={(e) => setBody(e.target.value)} rows={Math.min(8, ep.body_fields.length + 2)} className="font-mono text-xs" />
        </div>
      )}
      <Button size="sm" data-testid="tryit-send" onClick={send} disabled={sending} className="bg-primary hover:bg-primary/90 text-primary-foreground">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Send request
      </Button>
      {resp && (
        <div data-testid="tryit-response">
          <div className="mb-1 text-xs font-medium text-slate-500">
            Status <span className={`rounded px-1.5 py-0.5 font-mono ${String(resp.status).startsWith("2") ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{resp.status}</span> · {resp.ms} ms
          </div>
          <pre className="max-h-72 overflow-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">{JSON.stringify(resp.data, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

function ApiReference() {
  const [groups, setGroups] = useState([]);
  const [openEp, setOpenEp] = useState(null);
  const [openGroup, setOpenGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/developer/api-docs")
      .then((r) => { setGroups(r.data.groups || []); setOpenGroup((r.data.groups || [])[0]?.tag || null); })
      .catch(() => toast.error("Could not load API reference."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>;

  return (
    <div className="space-y-3" data-testid="api-reference">
      {groups.map((g) => (
        <Card key={g.tag} className="border-slate-200 shadow-sm overflow-hidden">
          <button data-testid={`api-group-${g.tag}`} onClick={() => setOpenGroup((x) => (x === g.tag ? null : g.tag))}
            className="flex w-full items-center justify-between px-4 py-3 hover:bg-slate-50">
            <span className="font-heading text-sm font-semibold text-slate-800">{g.tag}</span>
            <span className="flex items-center gap-2 text-xs text-slate-400"><span>{g.endpoints.length} endpoints</span><ChevronRight className={`h-4 w-4 transition-transform ${openGroup === g.tag ? "rotate-90" : ""}`} /></span>
          </button>
          {openGroup === g.tag && (
            <div className="divide-y divide-slate-100 border-t border-slate-100">
              {g.endpoints.map((ep, i) => {
                const id = `${ep.method}-${ep.path}`;
                const isOpen = openEp === id;
                return (
                  <div key={id} data-testid={`api-ep-${i}`}>
                    <button onClick={() => setOpenEp(isOpen ? null : id)} className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50">
                      <span className={`w-14 shrink-0 rounded px-1.5 py-0.5 text-center text-[11px] font-bold ${METHOD_COLOR[ep.method] || "bg-slate-100 text-slate-600"}`}>{ep.method}</span>
                      <span className="flex-1 font-mono text-xs text-slate-700">{ep.path}</span>
                      <ChevronRight className={`h-4 w-4 text-slate-300 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    </button>
                    {isOpen && <TryIt ep={ep} />}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

export default function Developer() {
  const [keys, setKeys] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);

  const load = () => api.get("/api-keys").then((r) => setKeys(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error("Name your key"); return; }
    try { const { data } = await api.post("/api-keys", { name }); setCreated(data); setName(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const revoke = async () => {
    try { await api.delete(`/api-keys/${revokeTarget.key_id}`); toast.success("Key revoked"); setRevokeTarget(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const copyKey = () => { navigator.clipboard.writeText(created.key); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const closeCreate = () => { setOpen(false); setCreated(null); setName(""); setCopied(false); };

  return (
    <div data-testid="developer-page">
      <PageHeader testid="developer-header" title="Developer Portal" subtitle="Browse the API reference, test endpoints live, and manage API keys." />

      <Tabs defaultValue="reference" className="mt-2">
        <TabsList>
          <TabsTrigger value="reference" data-testid="dev-tab-reference"><BookOpen className="h-4 w-4" /> API Reference</TabsTrigger>
          <TabsTrigger value="keys" data-testid="dev-tab-keys"><KeyRound className="h-4 w-4" /> API Keys</TabsTrigger>
        </TabsList>

        <TabsContent value="reference">
          <Card className="mb-3 border-slate-200 shadow-sm p-4">
            <div className="flex items-center gap-2"><Terminal className="h-4 w-4 text-slate-500" /><h3 className="font-heading text-sm font-semibold text-slate-800">Authentication</h3></div>
            <p className="mt-1 text-sm text-slate-500">Send your key in the <code className="rounded bg-slate-100 px-1 text-xs">X-API-Key</code> header. The "Send request" buttons below use your current logged-in session.</p>
            <pre className="mt-2 overflow-x-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100" data-testid="developer-snippet">{`curl ${API}/developer/whoami \\
  -H "X-API-Key: ipk_your_key_here"`}</pre>
          </Card>
          <ApiReference />
        </TabsContent>

        <TabsContent value="keys">
          <div className="mb-3 flex justify-end">
            <Button data-testid="new-key-button" onClick={() => setOpen(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground"><Plus className="h-4 w-4" /> Create API key</Button>
          </div>
          <Card className="border-slate-200 shadow-sm p-5">
            <h3 className="mb-3 font-heading text-sm font-semibold text-slate-800">Your API keys</h3>
            {keys.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-400" data-testid="keys-empty">No API keys yet. Create one to get started.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {keys.map((k) => (
                  <div key={k.key_id} className="flex items-center justify-between gap-3 py-3" data-testid={`api-key-${k.key_id}`}>
                    <div className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#eef2fb] text-primary"><KeyRound className="h-4 w-4" /></span>
                      <div>
                        <div className="text-sm font-medium text-slate-800">{k.name}</div>
                        <div className="font-mono text-xs text-slate-400">{k.prefix}••••••••  ·  last used {k.last_used ? new Date(k.last_used).toLocaleDateString() : "never"}</div>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" data-testid={`revoke-${k.key_id}`} onClick={() => setRevokeTarget(k)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /> Revoke</Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeCreate())}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{created ? "API key created" : "Create API key"}</DialogTitle>
            <DialogDescription>{created ? "Copy your key now — it won't be shown again." : "Give your key a descriptive name."}</DialogDescription>
          </DialogHeader>
          {created ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><div className="font-mono text-sm break-all text-slate-800" data-testid="created-key-value">{created.key}</div></div>
              <Button onClick={copyKey} variant="outline" className="w-full" data-testid="copy-key-button">{copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy key"}</Button>
            </div>
          ) : (
            <div className="space-y-1.5"><Label>Key name</Label><Input data-testid="key-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. CI pipeline" /></div>
          )}
          <DialogFooter>
            {created ? (
              <Button onClick={closeCreate} data-testid="key-done-button" className="bg-primary hover:bg-primary/90 text-primary-foreground">Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={closeCreate}>Cancel</Button>
                <Button data-testid="create-key-confirm" onClick={create} className="bg-primary hover:bg-primary/90 text-primary-foreground">Create</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Revoke API key?</AlertDialogTitle>
            <AlertDialogDescription>"{revokeTarget?.name}" will stop working immediately.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-revoke-key" onClick={revoke} className="bg-red-600 hover:bg-red-700">Revoke</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
